/**
 * Encode each path segment of a media URL so S3/CloudFront keys that contain
 * literal percent-sequences (e.g. Capture%20d%E2%80%99%C3%A9cran….png) are
 * requested as stored.
 *
 * Do not use the URL constructor for the path: it decodes %XX first, which
 * would turn a stored key of "Capture%20…" back into "Capture …" and undo
 * the fix. Skip segments that already contain %25 to avoid triple-encoding.
 */
export function encodeMediaUrl(url: string | null | undefined): string {
  if (!url) return "";

  const match = url.match(/^(https?:\/\/[^/]+)(\/[^?#]*)(.*)$/i);
  if (!match) return url;

  const [, origin, path, rest] = match;
  const encodedPath = path.split("/").map(encodeMediaPathSegment).join("/");

  return `${origin}${encodedPath}${rest}`;
}

export function encodeMediaUrlsInHtml(html: string | null | undefined): string {
  if (!html) return "";

  return html.replace(/\b(src|href)=(["'])(https?:\/\/[^"']+)\2/gi, (_match, attr: string, quote: string, url: string) => {
    return `${attr}=${quote}${encodeMediaUrl(url)}${quote}`;
  });
}

function isAscii(value: string): boolean {
  return /^[\x00-\x7F]*$/.test(value);
}

function encodeMediaPathSegment(segment: string): string {
  if (!segment) return segment;

  if (/%[0-9A-Fa-f]{2}/.test(segment)) {
    try {
      const decodedOnce = decodeURIComponent(segment);

      // Extra encode of ASCII reserved chars (`%2528` → `%28` → `(`).
      if (/%25[0-9A-Fa-f]{2}/i.test(segment)) {
        try {
          const decodedTwice = decodeURIComponent(decodedOnce);
          if (decodedTwice !== decodedOnce && isAscii(decodedOnce) && isAscii(decodedTwice)) {
            return decodedOnce;
          }
        } catch {
          /* keep */
        }
        return segment;
      }

      // Percent-encoded UTF-8 stored as the S3 key (macOS "Capture d’écran").
      if (decodedOnce !== segment && !isAscii(decodedOnce)) {
        return encodeURIComponent(segment);
      }
    } catch {
      return encodeURIComponent(segment);
    }
    // Already a valid encoding of an ASCII filename (`%20`, `%28`).
    return segment;
  }

  return encodeURIComponent(segment);
}
